import React, { useEffect, useRef, useState } from "react";
import { Form, Input, Button, message } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./login.css";
import { useDispatch } from "react-redux";
import {
  clearRedux,
  emailVerificationSuccess,
  loginSuccess,
} from "../../store/actions";
import axios from "axios";
import APICaller, { resetConnectionErrorFlag } from "../../utils/APICaller";
import {
  encryptData,
  encryptCryptoData,
  decrypt,
} from "../../components/Constants";
import { jwtDecode } from "jwt-decode";

export default function Login() {
  const [form] = Form.useForm();
  const loginInProgress = useRef(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const navigateHome = (role) => {
    navigate("/dashboard");
  };

  const handleLogin = async (values) => {
    try {
      if (loginInProgress.current) return;

      loginInProgress.current = true;
      setLoading(true);

      const encryptedPayload = await encryptCryptoData({
        username: values.username.trim(),
        password: values.password,
      });

      const endpoint = "/login"; // or your ldap login api
      const method = "POST";

      const response = await APICaller(
        "",
        "",
        endpoint,
        method,
        {
          auth: encryptedPayload,
        },
        "",
      );

      if (response.statusCode !== 200) {
        throw new Error(response.message || "Login failed");
      }

      // Decrypt response
      const decryptedData = decrypt(
        response.respnse,
        process.env.REACT_APP_ENCRYPTION_KEY,
      );

      if (!decryptedData) {
        throw new Error("Decryption failed");
      }

      const decryptedObject = JSON.parse(decryptedData);
      const user = decryptedObject.user;
      const accessToken = decryptedObject.accessToken;
      const access = decryptedObject.access;

      // Decode JWT expiry
      const decoded = jwtDecode(accessToken);

      const expiryTime = decoded.exp * 1000;
      dispatch(
        loginSuccess({
          accessToken,
          accessTokenExpiresOn: expiryTime,
        }),
      );
      resetConnectionErrorFlag();
      dispatch(
        emailVerificationSuccess({
          isLoggedIn: true,
          lIUId: user?._id,
          role: user?.roleID?.name,
          name: user?.name,
          empID: user?.empID,
          profileImage: user?.profilePicUrl,
          userInfo: encryptData({
            user,
            access,
            userDepartment: decryptedObject.departmentNames ?? [],
            userDepartmentCode: decryptedObject.departmentCode ?? [],
          }),
        }),
      );

      navigate("/dashboard");
    } catch (error) {
      console.error("error:", error);
      if (!error.response) {
        message.error(error?.message || "Unable to login");
      }
    } finally {
      loginInProgress.current = false;
      setLoading(false);
    }
  };

  //Show notification using Ant Design's notification component
  const showNotification = (errorMsg, moreThan = "no") => {
    message.error({
      content: (
        <div style={{ whiteSpace: "pre-line" }}>
          {moreThan === "yes" ? (
            <strong>Errors occurred:</strong>
          ) : (
            <strong>An error occurred:</strong>
          )}
          <p>{errorMsg}</p> {/* This ensures line breaks are respected */}
        </div>
      ),
      duration: 5, // duration in seconds
      className: "error-message", // Add any custom styles you need
    });
  };

  useEffect(() => {
    if (sessionStorage.getItem("sessionExpired")) {
      message.warning("Your session has expired. Please sign in again.");
      sessionStorage.removeItem("sessionExpired");
    }
  }, []);
  return (
    <>
      <div className="login_box">
        <div className="login-wrap">
          <div className="login-page-logo">
            <img src="/logo.svg" style={{ width: "200px" }} alt="logo" />
            <span> &nbsp;</span>
            <span> &nbsp;</span>
            <span className="separator"></span>
            <span className="expense_logo">
              &nbsp;Expense <br />
              Management System
            </span>
          </div>
          <Form form={form} layout="vertical" onFinish={handleLogin}>
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: "Username is required",
                },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Username" />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: "Password is required",
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              disabled={loading}
            >
              Sign In
            </Button>
          </Form>
        </div>
      </div>
    </>
  );
}
